import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { initModeler, initViewer, destroyViewer, destroyModeler, setDefaultVersionInfo, createDiagram } from '../../utils/bpmnModeler';
import VersionHistory from '../VersionHistory/VersionHistory';
import BausteinPickerDialog from '../BausteinPickerDialog/BausteinPickerDialog';
import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-js.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';
import '@bpmn-io/properties-panel/dist/assets/properties-panel.css';
import 'bpmn-js-token-simulation/assets/css/bpmn-js-token-simulation.css';
import 'diagram-js-minimap/assets/diagram-js-minimap.css';
import 'bpmn-js-color-picker/colors/color-picker.css';
import './BpmnEditor.css';
import { OPEN_EVENT, SELECT_EVENT } from '../../utils/bausteine';

const defaultDiagram = `<?xml version="1.0" encoding="UTF-8"?>
<definitions xmlns="http://www.omg.org/spec/BPMN/20100524/MODEL"
             xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
             xmlns:di="http://www.omg.org/spec/DD/20100524/DI"
             xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"
             id="Definitions_1"
             targetNamespace="http://bpmn.io/schema/bpmn">
  <process id="Process_1" isExecutable="true">
    <startEvent id="StartEvent_1" name="Start" />
  </process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Process_1">
      <bpmndi:BPMNShape id="StartEvent_1_di" bpmnElement="StartEvent_1">
        <dc:Bounds x="180" y="160" width="36" height="36" />
        <bpmndi:BPMNLabel>
          <dc:Bounds x="186" y="203" width="24" height="14" />
        </bpmndi:BPMNLabel>
      </bpmndi:BPMNShape>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</definitions>`;

const BpmnEditor = ({ onModelerReady, mode, modeler: modelerProp, viewingVersion, onExitVersionView, onViewVersion, viewingXml, restoringXml, onRestoreComplete, gridVisible }) => {
  const containerRef = useRef(null);
  const propertiesPanelRef = useRef(null);
  const modelerRef = useRef(null);
  const viewerRef = useRef(null);
  const currentXmlRef = useRef(null);
  const targetPlaneIdRef = useRef(null);
  const [initialized, setInitialized] = useState(false);
  const [versionInfo, setVersionInfo] = useState(null);
  const [planeNotFound, setPlaneNotFound] = useState(null);
  const gridVisibleRef = useRef(gridVisible);
  // Welcher Baustein gerade offen ist — null heisst: das Hauptdiagramm.
  const [offenerBaustein, setOffenerBaustein] = useState(null);
  // Offene Auswahlanfrage von der Zeichenflaeche: Liste, Art und die Funktion,
  // mit der dort weitergemacht wird.
  const [bausteinWahl, setBausteinWahl] = useState(null);

  // Keep gridVisibleRef in sync
  useEffect(() => {
    gridVisibleRef.current = gridVisible;
  }, [gridVisible]);

  // Sync grid visibility on an instance (modeler or viewer)
  const syncGrid = useCallback((instance) => {
    if (!instance) return;
    try {
      const grid = instance.get('grid');
      if (grid) grid.toggle(gridVisibleRef.current);
    } catch (e) {
      // Ein fehlendes Modul ist hier kein Grund abzustuerzen, aber es still
      // zu schlucken hat den Knopf im Ansichtsmodus lange tot aussehen
      // lassen, ohne eine Spur zu hinterlassen.
      console.warn('Raster laesst sich auf dieser Instanz nicht schalten:', e);
    }
  }, []);

  // Keep modelerRef in sync when the modeler is recreated externally (Load / New)
  useEffect(() => {
    if (!modelerProp) return;
    // Only sync if we're in edit mode and the prop differs from our ref
    if (mode === 'edit' && modelerProp !== modelerRef.current && modelerProp !== viewerRef.current) {
      modelerRef.current = modelerProp;
      syncGrid(modelerProp);
      // Also mark as initialized so mode-switch works correctly
      if (!initialized) setInitialized(true);
    }
  }, [modelerProp, mode, initialized, syncGrid]);

  // Robust reader for custom moddle attributes (with $attrs fallback)
  const readCustomAttr = (definitions, name) => {
    let val;
    try { val = definitions.get('custom:' + name); } catch { /* moddle may not support get */ }
    if (val === undefined || val === null) {
      val = definitions.$attrs?.['custom:' + name];
    }
    if (val === undefined || val === null) {
      // Try direct property access (moddle stores as local name)
      val = definitions[name];
    }
    return val;
  };

  // Extract version info from a modeler/viewer instance
  const extractVersionInfo = (instance) => {
    if (!instance?.getDefinitions) return null;
    try {
      const definitions = instance.getDefinitions();
      if (!definitions) return null;
      const version = readCustomAttr(definitions, 'version') || '1.0';
      const author = readCustomAttr(definitions, 'author') || '';
      let history = [];
      const historyStr = readCustomAttr(definitions, 'versionHistory');
      try {
        history = JSON.parse(historyStr || '[]');
      } catch { /* invalid JSON in history */ }
      return { version, author, history };
    } catch (e) {
      console.warn('Could not extract version info:', e);
      return null;
    }
  };

  // Initial modeler setup
  useEffect(() => {
    if (!containerRef.current || !propertiesPanelRef.current) return;
    if (modelerRef.current) return; // Already initialized

    // Initialize modeler with properties panel container
    const modeler = initModeler(containerRef.current, propertiesPanelRef.current);
    modelerRef.current = modeler;

    // Wait for the canvas to be fully initialized before loading diagram
    const timer = setTimeout(() => {
      console.log('Starting to load BPMN diagram...');
      
      modeler.importXML(defaultDiagram)
        .then(() => {
          console.log('BPMN diagram loaded successfully');
          
          // Center and zoom to fit the canvas
          const canvas = modeler.get('canvas');
      canvas.zoom('fit-viewport');
      
      console.log('Canvas zoomed to fit viewport');
      setDefaultVersionInfo(modeler);
      syncGrid(modeler);
      setInitialized(true);
          
          if (onModelerReady) {
            onModelerReady(modeler);
          }
        })
        .catch((err) => {
          console.error('Error loading BPMN diagram:', err);
          if (err.warnings) {
            console.error('Error warnings:', err.warnings);
          }
        });
    }, 200);

    return () => {
      clearTimeout(timer);
      if (modelerRef.current) {
        modelerRef.current.destroy();
        modelerRef.current = null;
      }
      if (viewerRef.current) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
    };
  }, [onModelerReady]);

  // Sync grid visibility with the diagram-js-grid module
  useEffect(() => {
    const instance = modelerRef.current || viewerRef.current;
    syncGrid(instance);
  }, [gridVisible, syncGrid]);

  /**
   * Auf ein anderes BPMN-Diagramm derselben Datei umschalten.
   *
   * bpmn-js zeigt immer nur die Ebenen **eines** Diagramms; ein Baustein liegt
   * in einem eigenen. `open()` leert die gezeichnete Flaeche und baut sie aus
   * `_definitions` neu auf — Modellaenderungen ueberleben also, der
   * Rueckgaengig-Verlauf nicht. Darum die Warnung, bevor etwas verloren geht.
   */
  const wechsleDiagramm = useCallback(async (diagramId, baustein) => {
    const modeler = modelerRef.current;
    const viewer = viewerRef.current;

    // Im Ansichtsmodus gibt es keinen Modeler mehr — er wird beim Wechsel
    // zerstoert. Hier traegt `open()`, anders als im Editor: Der Betrachter
    // hat kein Eigenschaften-Panel, und genau das stolperte dort ueber den
    // Wechsel. Derselbe Weg, den der PDF-Export schon geht.
    if (!modeler && viewer?.open) {
      try {
        await viewer.open(diagramId);
        viewer.get('canvas').zoom('fit-viewport');
        syncGrid(viewer);
        setOffenerBaustein(baustein);
      } catch (e) {
        console.error('[Baustein] Ebenenwechsel im Ansichtsmodus fehlgeschlagen:', e);
        alert('Die Ebene konnte nicht geöffnet werden: ' + e.message);
      }
      return;
    }

    if (!modeler?.saveXML) return;
    try {
      const stack = modeler.get('commandStack');
      if (stack?.canUndo?.() && !globalThis.confirm(
        'Beim Wechsel der Ebene geht der Rückgängig-Verlauf verloren. Die Änderungen selbst bleiben erhalten.\n\nFortfahren?'
      )) return;
    } catch { /* kein Kommandostapel — dann auch nichts zu verlieren */ }

    try {
      // Weder `modeler.open()` noch ein Neuimport in denselben Modeler haben
      // getragen: Beide lassen Reste des vorigen Diagramms zurueck, ueber die
      // das Eigenschaften-Panel stolpert (gemessen, auch mit einer von Hand
      // geschriebenen sauberen Datei). Darum derselbe Weg wie beim Laden einer
      // Datei — Modeler verwerfen und neu aufbauen, nur eben mit dem Diagramm
      // des Bausteins. Die Aenderungen stehen im Modell und reisen ueber das
      // serialisierte XML mit.
      const { xml } = await modeler.saveXML({ format: true });
      const neuerModeler = await createDiagram(xml, diagramId);
      modelerRef.current = neuerModeler;
      syncGrid(neuerModeler);
      setOffenerBaustein(baustein);
      if (onModelerReady) onModelerReady(neuerModeler);
    } catch (e) {
      console.error('[Baustein] Ebenenwechsel fehlgeschlagen:', e);
      alert('Die Ebene konnte nicht geöffnet werden: ' + e.message);
    }
  }, [syncGrid, onModelerReady]);

  /** Zurueck auf das erste Diagramm der Datei — das Hauptdiagramm. */
  const zurueckZumHauptdiagramm = useCallback(async () => {
    const instanz = modelerRef.current || viewerRef.current;
    const haupt = instanz?.getDefinitions?.()?.diagrams?.[0];
    if (haupt) await wechsleDiagramm(haupt.id, null);
  }, [wechsleDiagramm]);

  // Ein Moduswechsel oder eine neu geladene Datei zeigt immer das
  // Hauptdiagramm — die Leiste duerfte sonst einen Baustein melden, der gar
  // nicht mehr zu sehen ist.
  useEffect(() => {
    setOffenerBaustein(null);
  }, [mode, viewingXml]);

  // Der BausteinNavigationProvider kann nicht selbst umschalten — er kennt den
  // Modeler nicht, nur den Injector. Er meldet den Wunsch, hier wird er erfuellt.
  //
  // `modelerProp` gehoert in die Abhaengigkeiten: Beim Laden einer Datei wird
  // der Modeler samt Ereignisbus neu gebaut. Ohne das haengt die Anmeldung am
  // alten Bus, und der Knopf am Baustein bleibt wirkungslos.
  useEffect(() => {
    const instance = modelerRef.current || viewerRef.current;
    if (!instance?.get) return undefined;
    let eventBus;
    try { eventBus = instance.get('eventBus'); } catch { return undefined; }
    const auf = (e) => { wechsleDiagramm(e.diagramId, { id: e.processId, name: e.name }); };
    // `true` zurueckgeben heisst: Die Anfrage ist angenommen, der Provider
    // wartet auf `resolve`. Ohne das faellt er auf die Nummerneingabe zurueck.
    const waehle = (e) => {
      setBausteinWahl({ bausteine: e.bausteine, art: e.art, resolve: e.resolve });
      return true;
    };
    eventBus.on(OPEN_EVENT, auf);
    eventBus.on(SELECT_EVENT, waehle);
    return () => {
      try { eventBus.off(OPEN_EVENT, auf); eventBus.off(SELECT_EVENT, waehle); }
      catch { /* Bus schon fort */ }
    };
  }, [wechsleDiagramm, initialized, mode, modelerProp, viewingXml]);

  // Navigate to a previously active sub-process plane after reimport
  const navigateToTargetPlane = useCallback((canvas) => {
    const planeId = targetPlaneIdRef.current;
    targetPlaneIdRef.current = null;
    if (!planeId) return;

    // Main root — no navigation needed
    const allRoots = canvas.getRootElements ? canvas.getRootElements() : [];
    const mainRoot = allRoots.find((r) => !r.id.endsWith('_plane'));
    if (mainRoot && planeId === mainRoot.id) return;

    // Try to find the target plane
    const target = allRoots.find((r) => r.id === planeId);
    if (target) {
      canvas.setRootElement(target);
      canvas.zoom('fit-viewport');
    } else {
      // Plane not found — extract a readable name from the plane ID
      const readableName = planeId.replace(/_plane$/, '').replaceAll('_', ' ');
      setPlaneNotFound(readableName);
    }
  }, []);

  // Handle mode switching
  useEffect(() => {
    if (!initialized || !containerRef.current) return;

    const switchToView = async () => {
      try {
        setVersionInfo(null);
        setPlaneNotFound(null);

        // Capture current plane before destroying modeler
        if (viewingXml && modelerRef.current) {
          try {
            const canvas = modelerRef.current.get('canvas');
            targetPlaneIdRef.current = canvas.getRootElement()?.id || null;
          } catch { targetPlaneIdRef.current = null; }
        }

        // Export current XML from modeler
        if (modelerRef.current) {
          const { xml } = await modelerRef.current.saveXML({ format: true });
          currentXmlRef.current = xml;

          // Destroy modeler (only via singleton to avoid double-destroy)
          modelerRef.current = null;
          destroyModeler();
        }

        // Create viewer
        const viewer = initViewer(containerRef.current);
        viewerRef.current = viewer;

        // If viewing a historic version, use that XML; otherwise use current
        const xmlToLoad = viewingXml || currentXmlRef.current;
        await viewer.importXML(xmlToLoad);
        const canvas = viewer.get('canvas');
        canvas.zoom('fit-viewport');

        // Try to navigate to the same plane as before
        navigateToTargetPlane(canvas);

        // Extract and store version info in state immediately after import
        const info = extractVersionInfo(viewer);
        setVersionInfo(info);

        // Notify parent so event listeners (e.g. videoPlayer.seekTo) are registered on viewer
        if (onModelerReady) {
          onModelerReady(viewer);
        }

        // Das Raster startet je diagram.init eingeschaltet — hier auf den
        // Zustand des Knopfes nachziehen, sonst zeigt der Ansichtsmodus ein
        // Raster, das die Werkzeugleiste als ausgeschaltet meldet.
        syncGrid(viewer);

        console.log('Switched to View mode', viewingXml ? '(historic version)' : '');
      } catch (err) {
        console.error('Error switching to view mode:', err);
      }
    };

    const switchToEdit = async () => {
      try {
        setVersionInfo(null);

        // Export current XML from viewer (or use restore backup if available)
        if (viewerRef.current) {
          if (restoringXml) {
            // Use the backed-up XML from before version view
            currentXmlRef.current = restoringXml;
          } else {
            const { xml } = await viewerRef.current.saveXML({ format: true });
            currentXmlRef.current = xml;
          }

          // Destroy viewer (only via singleton to avoid double-destroy)
          viewerRef.current = null;
          destroyViewer();
        }

        // Recreate modeler
        const modeler = initModeler(containerRef.current, propertiesPanelRef.current);
        modelerRef.current = modeler;

        // Import XML into modeler
        await modeler.importXML(currentXmlRef.current);
        const canvas = modeler.get('canvas');
        canvas.zoom('fit-viewport');
        syncGrid(modeler);

        if (onModelerReady) {
          onModelerReady(modeler);
        }
        if (onRestoreComplete) onRestoreComplete();

        console.log('Switched to Edit mode');
      } catch (err) {
        console.error('Error switching to edit mode:', err);
        if (onRestoreComplete) onRestoreComplete();
      }
    };

    if (mode === 'view') {
      switchToView();
    } else if (mode === 'edit' && viewerRef.current) {
      // Only switch back if we were in view mode (viewer exists)
      switchToEdit();
    }
  }, [mode, initialized, onModelerReady]);

  // When viewingXml changes while already in view mode, re-import into existing viewer
  useEffect(() => {
    if (!viewingXml || !viewerRef.current) return;
    const reimport = async () => {
      try {
        // Capture current plane and XML before reimport
        const canvas = viewerRef.current.get('canvas');
        const currentPlaneId = canvas.getRootElement()?.id ?? null;
        targetPlaneIdRef.current = currentPlaneId;

        // Check if target plane exists in new XML before importing
        if (currentPlaneId?.endsWith('_plane')) {
          const subProcessId = currentPlaneId.replace(/_plane$/, '');
          if (!viewingXml.includes(`id="${subProcessId}"`)) {
            // Plane won't exist in this version — show modal, don't change view
            const readableName = subProcessId.replaceAll('_', ' ');
            setPlaneNotFound(readableName);
            targetPlaneIdRef.current = null;
            return; // abort — don't import
          }
        }

        await viewerRef.current.importXML(viewingXml);
        canvas.zoom('fit-viewport');

        // Navigate to the same plane
        navigateToTargetPlane(canvas);

        // Don't overwrite versionInfo here — historic snapshots have
        // versionHistory stripped, so extractVersionInfo would return
        // an empty history and the panel would disappear.
      } catch (e) {
        console.error('Error loading historic version:', e);
      }
    };
    reimport();
  }, [viewingXml, navigateToTargetPlane]);

  // --- Version History panel resize logic ---
  const vhpPanelRef = useRef(null);
  const isVhpDragging = useRef(false);

  const onVhpResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    isVhpDragging.current = true;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev) => {
      if (!isVhpDragging.current || !vhpPanelRef.current) return;
      const containerRect = vhpPanelRef.current.parentElement.getBoundingClientRect();
      const newHeight = containerRect.bottom - ev.clientY;
      const maxHeight = containerRect.height * 0.6;
      const clamped = Math.max(80, Math.min(maxHeight, newHeight));
      vhpPanelRef.current.style.height = clamped + 'px';
      vhpPanelRef.current.style.maxHeight = clamped + 'px';
    };

    const onMouseUp = () => {
      isVhpDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  // --- Properties panel resize logic ---
  const panelRef = useRef(null);
  const resizeHandleRef = useRef(null);
  const isDragging = useRef(false);

  const onResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    if (resizeHandleRef.current) resizeHandleRef.current.classList.add('active');
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev) => {
      if (!isDragging.current || !panelRef.current) return;
      const containerRect = panelRef.current.parentElement.getBoundingClientRect();
      const newWidth = containerRect.right - ev.clientX;
      const clamped = Math.max(200, Math.min(600, newWidth));
      panelRef.current.style.width = clamped + 'px';
    };

    const onMouseUp = () => {
      isDragging.current = false;
      if (resizeHandleRef.current) resizeHandleRef.current.classList.remove('active');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const isViewMode = mode === 'view';

  return (
    <div className={`bpmn-editor-container ${isViewMode ? 'view-mode' : 'edit-mode'}`}>
      <div ref={containerRef} className="bpmn-editor" />
      <BausteinPickerDialog
        isOpen={!!bausteinWahl}
        bausteine={bausteinWahl?.bausteine ?? []}
        art={bausteinWahl?.art ?? 'verweis'}
        onWahl={(baustein) => { bausteinWahl?.resolve(baustein); setBausteinWahl(null); }}
        onAbbruch={() => { bausteinWahl?.resolve(null); setBausteinWahl(null); }}
      />
      {offenerBaustein && (
        <div className="baustein-leiste">
          <span className="baustein-leiste-text">
            Baustein: <strong>{offenerBaustein.name}</strong>
          </span>
          <button type="button" className="baustein-leiste-btn" onClick={zurueckZumHauptdiagramm}>
            ↩ Zurück zum Hauptdiagramm
          </button>
        </div>
      )}
      {!isViewMode && (
        <div className="bpmn-panel-wrapper">
          <button
            type="button"
            ref={resizeHandleRef}
            className="bpmn-panel-resize-handle"
            onMouseDown={onResizeMouseDown}
            aria-label="Panel-Größe anpassen"
          />
          <div
            ref={(el) => { propertiesPanelRef.current = el; panelRef.current = el; }}
            className="bpmn-properties-panel-container"
          />
        </div>
      )}
      {isViewMode && versionInfo && versionInfo.history.length > 0 && (
        <VersionHistory
          versionHistory={versionInfo.history}
          currentVersion={versionInfo.version}
          currentAuthor={versionInfo.author}
          onViewVersion={onViewVersion}
          viewingVersion={viewingVersion}
          onExitVersionView={onExitVersionView}
          onResizeMouseDown={onVhpResizeMouseDown}
          panelRef={vhpPanelRef}
        />
      )}
      {planeNotFound && (
        <div className="plane-not-found-overlay">
          <button type="button" className="plane-not-found-backdrop" onClick={() => setPlaneNotFound(null)} aria-label="Schließen" />
          <dialog className="plane-not-found-modal" open aria-label="Sub-Prozess nicht gefunden">
            <div className="plane-not-found-icon"><i className="bi bi-exclamation-triangle"></i></div>
            <h6>Sub-Prozess nicht gefunden</h6>
            <p>
              Der Sub-Prozess <strong>{planeNotFound}</strong> existiert in dieser Version noch nicht.
              Die Hauptebene wird stattdessen angezeigt.
            </p>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setPlaneNotFound(null)}>
              Verstanden
            </button>
          </dialog>
        </div>
      )}
    </div>
  );
};

BpmnEditor.propTypes = {
  onModelerReady: PropTypes.func,
  mode: PropTypes.string.isRequired,
  modeler: PropTypes.object,
  viewingVersion: PropTypes.string,
  onExitVersionView: PropTypes.func,
  onViewVersion: PropTypes.func,
  viewingXml: PropTypes.string,
  restoringXml: PropTypes.string,
  onRestoreComplete: PropTypes.func,
  gridVisible: PropTypes.bool,
};

export default BpmnEditor;
